import { useEffect, useState } from "react";
import Shell from "../components/Shell.jsx";
import OdometerSnapButton from "../components/OdometerSnap.jsx";
import { api } from "../lib/api.js";
import { Field, SelectWithOther, SaveButton, inputCls } from "../components/ui.jsx";
import { ContactPicker } from "./Crm.jsx";
import { localToday, hmPlus } from "../lib/dates.js";

const PURPOSES = ["Client visit", "Training / workshop", "Networking event", "Errand / supplies", "Bank / post office"];

const defaultDoneTime = (hoursAhead = 2) => hmPlus(hoursAhead);
const fmtMoney = (cents) => `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StartTripForm({ vehicles, snap, onDone, onCancel }) {
  const guessed = snap?.vehicle_guess && vehicles.includes(snap.vehicle_guess) ? snap.vehicle_guess : null;
  const [f, setF] = useState({
    start_odo: snap?.reading != null ? String(snap.reading) : "",
    vehicle_name: guessed || vehicles[0] || "",
    purpose: "",
    destination: "",
    date: localToday(),
    done_time: defaultDoneTime(),
  });
  const [contact, setContact] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const lowConfidence = snap && (snap.reading == null || (snap.confidence ?? 0) < 0.7);

  function pickContact(c) {
    setContact(c);
    // A picked client fills the destination if it's still blank.
    if (c) setF((s) => ({ ...s, destination: s.destination || c.company || c.name }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!f.purpose.trim()) return setError("Purpose is required — it's what makes the miles deductible.");
    setBusy(true);
    setError("");
    try {
      await api("/api/mileage/trips", {
        method: "POST",
        body: {
          date: f.date,
          purpose: f.purpose,
          destination: f.destination || null,
          contact_id: contact?.id || null,
          vehicle_name: f.vehicle_name || null,
          start_odo: f.start_odo === "" ? null : Number(f.start_odo),
          start_photo_key: snap?.photo_key || null,
          expected_end: f.done_time ? `${f.date} ${f.done_time}` : null,
        },
      });
      onDone();
    } catch (err) {
      setError(err.message || "Save failed");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl bg-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-slate-700">🚗 Start trip</p>
      {snap && (
        <p className={`rounded-xl px-4 py-2 text-sm ${lowConfidence ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}>
          {lowConfidence
            ? snap.ocr_error
              ? "⚠️ The odometer reader hit an error — just type the number below."
              : "⚠️ Couldn't read the odometer confidently — check the number."
            : "Odometer read ✓ — confirm and go."}
          {guessed ? ` Looks like the ${guessed}.` : ""}
          {snap.ocr_error ? <span className="mt-1 block text-xs opacity-70">{snap.ocr_error}</span> : null}
        </p>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Odometer at start">
          <input inputMode="decimal" value={f.start_odo} onChange={set("start_odo")} className={inputCls} placeholder="e.g. 48213" />
        </Field>
        <Field label="Vehicle">
          <SelectWithOther value={f.vehicle_name} onChange={(v) => setF((s) => ({ ...s, vehicle_name: v }))} options={vehicles} placeholder="Pick vehicle…" />
        </Field>
      </div>
      <Field label="Purpose (required — IRS log)">
        <SelectWithOther value={f.purpose} onChange={(v) => setF((s) => ({ ...s, purpose: v }))} options={PURPOSES} placeholder="Why this trip?" />
      </Field>
      <Field label="Client (optional)">
        <ContactPicker value={contact} onChange={pickContact} placeholder="Link a client…" allowCreate />
      </Field>
      <Field label="Destination">
        <input value={f.destination} onChange={set("destination")} className={inputCls} placeholder="e.g. Capstone Rail, Muncie" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date">
          <input type="date" value={f.date} onChange={set("date")} className={inputCls} />
        </Field>
        <Field label="Done driving by" hint="You'll get a reminder to snap the end reading.">
          <input type="time" value={f.done_time} onChange={set("done_time")} className={inputCls} />
        </Field>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="flex-1 rounded-xl border border-slate-300 py-3 text-sm font-semibold text-slate-500">
          Cancel
        </button>
        <div className="flex-1">
          <SaveButton busy={busy}>Start trip</SaveButton>
        </div>
      </div>
    </form>
  );
}

function EndTripForm({ trip, snap, onDone, onCancel }) {
  const [endOdo, setEndOdo] = useState(snap?.reading != null ? String(snap.reading) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const lowConfidence = snap && (snap.reading == null || (snap.confidence ?? 0) < 0.7);
  const miles =
    endOdo !== "" && trip.start_odo != null && Number(endOdo) >= trip.start_odo
      ? Math.round((Number(endOdo) - trip.start_odo) * 10) / 10
      : null;

  async function submit(e) {
    e.preventDefault();
    if (endOdo === "") return setError("Enter the end reading.");
    setBusy(true);
    setError("");
    try {
      await api(`/api/mileage/trips/${trip.id}`, {
        method: "PUT",
        body: { end_odo: Number(endOdo), end_photo_key: snap?.photo_key || undefined },
      });
      onDone();
    } catch (err) {
      setError(err.message || "Save failed");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl bg-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-slate-700">🏁 End trip — {trip.purpose || trip.date}</p>
      {snap && (
        <p className={`rounded-xl px-4 py-2 text-sm ${lowConfidence ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}>
          {lowConfidence
            ? snap.ocr_error
              ? "⚠️ The odometer reader hit an error — just type the number below."
              : "⚠️ Couldn't read the odometer confidently — check the number."
            : "Odometer read ✓"}
          {snap.ocr_error ? <span className="mt-1 block text-xs opacity-70">{snap.ocr_error}</span> : null}
        </p>
      )}
      <Field label="Odometer at end">
        <input autoFocus inputMode="decimal" value={endOdo} onChange={(e) => setEndOdo(e.target.value)} className={inputCls} />
      </Field>
      {miles != null && (
        <p className="rounded-xl bg-navy-700/5 px-4 py-2 text-sm font-semibold text-navy-700">= {miles} miles</p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="flex-1 rounded-xl border border-slate-300 py-3 text-sm font-semibold text-slate-500">
          Cancel
        </button>
        <div className="flex-1">
          <SaveButton busy={busy}>Save trip</SaveButton>
        </div>
      </div>
    </form>
  );
}

// Backfill a trip that already happened, before the app existed — no
// odometer, just a remembered/reconstructed total. Always flagged so the
// trip log and CPA export can tell it apart from a live-tracked entry.
function PastTripForm({ vehicles, onDone, onCancel }) {
  const [f, setF] = useState({
    date: localToday(),
    purpose: "",
    destination: "",
    vehicle_name: vehicles[0] || "",
    miles: "",
    notes: "",
  });
  const [contact, setContact] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  function pickContact(c) {
    setContact(c);
    if (c) setF((s) => ({ ...s, destination: s.destination || c.company || c.name }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!f.purpose.trim()) return setError("Purpose is required — it's what makes the miles deductible.");
    if (!f.date) return setError("Date is required.");
    // Accept "1,200"-style input; reject anything that isn't a positive number
    // (NaN would silently serialize to null and create a phantom active trip).
    const milesNum = Number(String(f.miles).replace(/,/g, "").trim());
    if (f.miles === "" || !isFinite(milesNum) || milesNum <= 0)
      return setError("Enter the total miles as a number, e.g. 96 or 140.5.");
    setBusy(true);
    setError("");
    try {
      await api("/api/mileage/trips", {
        method: "POST",
        body: {
          date: f.date,
          purpose: f.purpose,
          destination: f.destination || null,
          contact_id: contact?.id || null,
          vehicle_name: f.vehicle_name || null,
          miles: milesNum,
          notes: f.notes || null,
          reconstructed: true,
        },
      });
      onDone();
    } catch (err) {
      setError(err.message || "Save failed");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl bg-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-slate-700">🕐 Log a past trip</p>
      <p className="text-xs text-slate-500">
        For trips before this app existed. Pull the date from your calendar or email if you can — that's stronger evidence than memory alone.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date">
          <input type="date" max={localToday()} value={f.date} onChange={set("date")} className={inputCls} />
        </Field>
        <Field label="Vehicle">
          <SelectWithOther value={f.vehicle_name} onChange={(v) => setF((s) => ({ ...s, vehicle_name: v }))} options={vehicles} placeholder="Pick vehicle…" />
        </Field>
      </div>
      <Field label="Purpose (required — IRS log)">
        <SelectWithOther value={f.purpose} onChange={(v) => setF((s) => ({ ...s, purpose: v }))} options={PURPOSES} placeholder="Why this trip?" />
      </Field>
      <Field label="Client (optional)">
        <ContactPicker value={contact} onChange={pickContact} placeholder="Link a client…" allowCreate />
      </Field>
      <Field label="Destination">
        <input value={f.destination} onChange={set("destination")} className={inputCls} placeholder="e.g. Indianapolis" />
      </Field>
      <Field label="Total miles (round trip)">
        <input inputMode="decimal" value={f.miles} onChange={set("miles")} className={inputCls} placeholder="e.g. 96" />
      </Field>
      <Field label="How you know this (optional)" hint="e.g. calendar invite, Maps timeline — strengthens the record.">
        <input value={f.notes} onChange={set("notes")} className={inputCls} placeholder="Reconstructed from…" />
      </Field>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="flex-1 rounded-xl border border-slate-300 py-3 text-sm font-semibold text-slate-500">
          Cancel
        </button>
        <div className="flex-1">
          <SaveButton busy={busy}>Log trip</SaveButton>
        </div>
      </div>
    </form>
  );
}

// Year-start odometer readings — the annual anchor for business-use %.
function BenchmarksCard({ bench, onSaved }) {
  const [draft, setDraft] = useState(null); // { vehicle, snap, reading }
  const [busy, setBusy] = useState(false);
  if (!bench || bench.vehicles.length === 0) return null;
  const missing = bench.vehicles.filter((v) => v.reading == null);

  async function save() {
    if (!draft || draft.reading === "") return;
    setBusy(true);
    try {
      await api("/api/mileage/benchmarks", {
        method: "POST",
        body: { vehicle_name: draft.vehicle, reading: Number(draft.reading), photo_key: draft.snap?.photo_key || null },
      });
      setDraft(null);
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={`rounded-2xl p-4 shadow-sm ${missing.length ? "border border-amber-200 bg-amber-50" : "bg-white"}`}>
      <p className={`text-sm font-semibold ${missing.length ? "text-amber-900" : "text-slate-700"}`}>
        📅 {bench.year} year-start odometer
      </p>
      <p className={`mt-0.5 text-xs ${missing.length ? "text-amber-800" : "text-slate-400"}`}>
        {missing.length
          ? "One reading per vehicle anchors the year's business-use % for the IRS."
          : "All vehicles anchored for this year ✓"}
      </p>
      <div className="mt-2 space-y-2">
        {bench.vehicles.map((v) =>
          draft?.vehicle === v.name ? (
            <div key={v.name} className="flex items-center gap-2">
              <input
                autoFocus
                inputMode="decimal"
                value={draft.reading}
                onChange={(e) => setDraft((d) => ({ ...d, reading: e.target.value }))}
                className={inputCls}
                placeholder={`${v.name} odometer`}
              />
              <button onClick={save} disabled={busy} className="shrink-0 rounded-xl bg-navy-700 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
                Save
              </button>
            </div>
          ) : v.reading != null ? (
            <p key={v.name} className="text-sm text-slate-600">
              ✓ {v.name} — {v.reading.toLocaleString()} mi
              <button onClick={() => setDraft({ vehicle: v.name, snap: null, reading: String(v.reading) })} className="ml-2 text-xs font-semibold text-navy-700">
                edit
              </button>
            </p>
          ) : (
            <OdometerSnapButton
              key={v.name}
              label={`📷 Snap ${v.name}'s odometer`}
              className="w-full rounded-xl border border-amber-300 bg-white py-2.5 text-sm font-semibold text-amber-900 disabled:opacity-60"
              onRead={(snap) => setDraft({ vehicle: v.name, snap, reading: snap.reading != null ? String(snap.reading) : "" })}
            />
          ),
        )}
      </div>
    </section>
  );
}

export default function Mileage() {
  const [data, setData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [bench, setBench] = useState(null);
  const [starting, setStarting] = useState(null); // { snap } | null
  const [ending, setEnding] = useState(null); // { trip, snap } | null
  const [loggingPast, setLoggingPast] = useState(false);

  const load = () => {
    api("/api/mileage/trips").then(setData).catch(() => setData({ active: [], trips: [], summary: null }));
    api("/api/mileage/vehicles").then((d) => setVehicles(d.vehicles || [])).catch(() => {});
    api("/api/mileage/benchmarks").then(setBench).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  if (!data) return <Shell title="Mileage"><p className="text-center text-sm text-slate-400">Loading…</p></Shell>;
  const { active, trips, summary } = data;

  return (
    <Shell title="Mileage">
      {/* Active trips first — ending a trip is the most likely reason to be here */}
      {active.map((t) =>
        ending?.trip.id === t.id ? (
          <EndTripForm key={t.id} trip={ending.trip} snap={ending.snap} onDone={() => { setEnding(null); load(); }} onCancel={() => setEnding(null)} />
        ) : (
          <section key={t.id} className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-900">🚗 Trip in progress — {t.purpose || t.date}</p>
            <p className="mt-0.5 text-xs text-amber-800">
              {t.vehicle_name ? `${t.vehicle_name} · ` : ""}
              {t.start_odo != null ? `started at ${t.start_odo} mi` : "no start reading"}
              {t.expected_end ? ` · done by ${t.expected_end.slice(11)}` : ""}
            </p>
            <div className="mt-3 flex gap-2">
              <OdometerSnapButton
                label="📷 Snap end odometer"
                className="flex-1 rounded-xl bg-navy-700 py-3 text-sm font-semibold text-white disabled:opacity-60"
                onRead={(snap) => setEnding({ trip: t, snap })}
              />
              <button
                onClick={() => setEnding({ trip: t, snap: null })}
                className="shrink-0 rounded-xl border border-amber-300 px-4 text-sm font-semibold text-amber-900"
              >
                Type it
              </button>
            </div>
            <button
              onClick={async () => {
                if (!window.confirm("Discard this trip?")) return;
                await api(`/api/mileage/trips/${t.id}`, { method: "DELETE" });
                load();
              }}
              className="mt-2 text-xs font-semibold text-amber-700"
            >
              Discard trip
            </button>
          </section>
        ),
      )}

      {/* Start a new trip */}
      {starting != null ? (
        <StartTripForm vehicles={vehicles} snap={starting.snap} onDone={() => { setStarting(null); load(); }} onCancel={() => setStarting(null)} />
      ) : loggingPast ? (
        <PastTripForm vehicles={vehicles} onDone={() => { setLoggingPast(false); load(); }} onCancel={() => setLoggingPast(false)} />
      ) : (
        <div className="space-y-2">
          <OdometerSnapButton
            label="📷 Snap odometer to start a trip"
            className="w-full rounded-2xl bg-navy-700 py-4 text-base font-semibold text-white shadow-sm disabled:opacity-60"
            onRead={(snap) => setStarting({ snap })}
          />
          <div className="flex justify-center gap-4">
            <button onClick={() => setStarting({ snap: null })} className="text-xs font-semibold text-slate-400">
              or start without a photo
            </button>
            <button onClick={() => setLoggingPast(true)} className="text-xs font-semibold text-slate-400">
              🕐 log a past trip
            </button>
          </div>
        </div>
      )}

      {/* YTD summary */}
      {summary && (
        <section className="rounded-2xl bg-white p-4 shadow-sm">
          <div className="flex items-baseline justify-between">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{summary.year} business miles</p>
            <p className="text-xs text-slate-400">{summary.trip_count} trip{summary.trip_count === 1 ? "" : "s"}</p>
          </div>
          <div className="mt-1 flex items-baseline gap-4">
            <p className="text-2xl font-semibold text-navy-700">{summary.business_miles.toLocaleString()} mi</p>
            {summary.rate_cents > 0 && (
              <p className="text-sm text-slate-500">
                ≈ <span className="font-semibold text-emerald-600">{fmtMoney(summary.deduction_cents)}</span> @ {summary.rate_cents}¢/mi
              </p>
            )}
          </div>
          <p className="mt-1 text-[11px] text-slate-400">
            Estimate — {summary.rate_verified ? "rate confirmed with CPA." : "rate not yet verified with CPA."}
          </p>
        </section>
      )}

      <BenchmarksCard bench={bench} onSaved={load} />

      {/* Trip log */}
      <section>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Trip log</p>
        {trips.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
            <p className="text-4xl">🚗</p>
            <p className="mt-3 font-medium text-slate-700">No trips yet</p>
            <p className="mt-1 text-sm text-slate-500">Snap your odometer to start the first one.</p>
          </div>
        )}
        {trips.length > 0 && (
          <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
            {trips.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">
                    {t.purpose || "Trip"}
                    {t.destination ? ` → ${t.destination}` : ""}
                    {t.reconstructed ? (
                      <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-500" title="Entered after the fact, not tracked live">
                        reconstructed
                      </span>
                    ) : null}
                  </p>
                  <p className="truncate text-xs text-slate-400">
                    {t.date}
                    {t.vehicle_name ? ` · ${t.vehicle_name}` : ""}
                    {t.contact_name ? ` · 👤 ${t.contact_name}` : ""}
                    {t.start_photo_key && (
                      <>
                        {" · "}
                        <a href={`/api/mileage/image?key=${encodeURIComponent(t.start_photo_key)}`} target="_blank" rel="noreferrer" className="text-navy-700">
                          📷 start
                        </a>
                      </>
                    )}
                    {t.end_photo_key && (
                      <>
                        {" "}
                        <a href={`/api/mileage/image?key=${encodeURIComponent(t.end_photo_key)}`} target="_blank" rel="noreferrer" className="text-navy-700">
                          📷 end
                        </a>
                      </>
                    )}
                  </p>
                </div>
                <p className={`ml-3 shrink-0 text-sm font-semibold ${t.status === "active" ? "text-amber-600" : "text-slate-700"}`}>
                  {t.status === "active" ? "open" : t.miles != null ? `${t.miles} mi` : "—"}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
      <p className="text-center text-xs text-slate-400">Estimates shown in this app are not tax advice — confirm with your CPA.</p>
    </Shell>
  );
}
